import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import * as Constants from "../../../utils/constants"
import { MetaforceContext, WebEditorContext } from "../../../contexts"
import { useContext, Fragment } from "react"
import { Popover, Transition } from "@headlessui/react"
import { EllipsisHorizontalCircleIcon } from "@heroicons/react/24/outline"
import { useTranslation } from 'react-i18next';
import { resources, translations } from "../../../translations/translationsConstants";
import { useQuill } from "../../../hooks/useQuill"
import { faAlignCenter, faAlignJustify, faAlignLeft, faAlignRight, faBold, faItalic, faList, faListOl, faUnderline } from "@fortawesome/free-solid-svg-icons"
import "./styles.scss"

export default function FormatMenuBar() {
    const { quillRef, formatMenuBar, isLoading, editorFileRef, setFormatMenuBar } = useContext(WebEditorContext);
    const { themeAndStyle } = useContext(MetaforceContext);
    const quill = quillRef.current;
    const classNames = (state, baseClassNames) => {
        if (state === Constants.formatButtonState.disabledAndUnfocused) {
            return `tw-px-2 tw-py-0.5 tw-bg-white tw-text-gray-400 tw-text-sm tw-cursor-not-allowed ${baseClassNames} menu-button-tooltip-container`;
        } else if (state === Constants.formatButtonState.disabledAndFocused) {
            return `tw-px-2 tw-py-0.5 tw-bg-white tw-text-gray-400 tw-text-sm tw-border tw-border-gray-300 tw-rounded-sm tw-cursor-not-allowed ${baseClassNames} menu-button-tooltip-container`;
        } else if (state === Constants.formatButtonState.enabledAndUnfocused) {
            return `tw-px-2 tw-py-0.5 tw-bg-white tw-text-gray-800 tw-text-sm ${baseClassNames} menu-button-tooltip-container`;
        } else if (state === Constants.formatButtonState.enabledAndFocused) {
            return `tw-px-2 tw-py-0.5 tw-bg-gray-200 text-gray-800 tw-text-sm tw-border tw-border-gray-300 tw-rounded-sm ${baseClassNames} menu-button-tooltip-container`;
        }
    }
    const { t: tc } = useTranslation([resources.common]);
    const { setBold, setItalic, setUnderline, alignLeft, alignRight, alignCenter, alignJustify, setUnorderedList, setNumberedList, changeFontSize } = useQuill();

    return (
        <div className="tw-flex tw-items-center tw-gap-x-3 tw-px-3">
            <div className="tw-flex tw-items-center tw-gap-x-1">
                <button
                    aria-label={tc(translations.common.format.bold)}
                    type="button"
                    className={classNames(formatMenuBar.boldButtonState)}
                    onClick={() => setBold(quill, setFormatMenuBar)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            setBold(quill, setFormatMenuBar);
                        }
                    }}
                    disabled={formatMenuBar.boldButtonState === Constants.formatButtonState.disabledAndFocused || formatMenuBar.boldButtonState === Constants.formatButtonState.disabledAndUnfocused || isLoading}
                >
                    <FontAwesomeIcon className="tw-h-4" size={'sm'} icon={faBold} />
                    <span role="tooltip" id="bold-tooltip-desc" className="menu-tooltip">{tc(translations.common.format.bold)}</span>
                </button>
                <button
                    aria-label={tc(translations.common.format.italic)}
                    type="button"
                    className={classNames(formatMenuBar.italicButtonState)}
                    onClick={() => setItalic(quill, setFormatMenuBar)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            setItalic(quill, setFormatMenuBar);
                        }
                    }}
                    disabled={formatMenuBar.italicButtonState === Constants.formatButtonState.disabledAndFocused || formatMenuBar.italicButtonState === Constants.formatButtonState.disabledAndUnfocused || isLoading}
                >
                    <FontAwesomeIcon className="tw-h-4" size={'sm'} icon={faItalic} />
                    <span role="tooltip" id="italic-tooltip-desc" className="menu-tooltip">{tc(translations.common.format.italic)}</span>
                </button>
                <button
                    aria-label={tc(translations.common.format.underline)}
                    type="button"
                    className={classNames(formatMenuBar.underlineButtonState)}
                    onClick={() => setUnderline(quill, setFormatMenuBar)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            setUnderline(quill, setFormatMenuBar);
                        }
                    }}
                    disabled={formatMenuBar.underlineButtonState === Constants.formatButtonState.disabledAndFocused || formatMenuBar.underlineButtonState === Constants.formatButtonState.disabledAndUnfocused || isLoading}
                >
                    <FontAwesomeIcon className="tw-h-4" size={'sm'} icon={faUnderline} />
                    <span role="tooltip" id="underline-tooltip-desc" className="menu-tooltip">{tc(translations.common.format.underline)}</span>
                </button>
            </div>

            <div className="tw-hidden sm:tw-flex tw-items-center tw-gap-x-1">
                <button
                    aria-label={tc(translations.common.format.alignLeft)}
                    type="button"
                    className={classNames(formatMenuBar.alignLeftButtonState, "tw-ml-1.5")}
                    onClick={() => alignLeft(quill)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            alignLeft(quill);
                        }
                    }}
                    disabled={formatMenuBar.alignLeftButtonState === Constants.formatButtonState.disabledAndFocused || formatMenuBar.alignLeftButtonState === Constants.formatButtonState.disabledAndUnfocused || isLoading}
                >
                    <FontAwesomeIcon className="tw-h-4" size={'sm'} icon={faAlignLeft} />
                    <span role="tooltip" id="alignleft-tooltip-desc" className="menu-tooltip">{tc(translations.common.format.alignLeft)}</span>
                </button>
                <button
                    aria-label={tc(translations.common.format.alignCenter)}
                    type="button"
                    className={classNames(formatMenuBar.alignCenterButtonState)}
                    onClick={() => alignCenter(quill)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            alignCenter(quill);
                        }
                    }}
                    disabled={formatMenuBar.alignCenterButtonState === Constants.formatButtonState.disabledAndFocused || formatMenuBar.alignCenterButtonState === Constants.formatButtonState.disabledAndUnfocused || isLoading}
                >
                    <FontAwesomeIcon className="tw-h-4" size={'sm'} icon={faAlignCenter} />
                    <span role="tooltip" id="aligncenter-tooltip-desc" className="menu-tooltip">{tc(translations.common.format.alignCenter)}</span>
                </button>
                <button
                    aria-label={tc(translations.common.format.alignRight)}
                    type="button"
                    className={classNames(formatMenuBar.alignRightButtonState)}
                    onClick={() => alignRight(quill)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            alignRight(quill);
                        }
                    }}
                    disabled={formatMenuBar.alignRightButtonState === Constants.formatButtonState.disabledAndFocused || formatMenuBar.alignRightButtonState === Constants.formatButtonState.disabledAndUnfocused || isLoading}
                >
                    <FontAwesomeIcon className="tw-h-4" size={'sm'} icon={faAlignRight} />
                    <span role="tooltip" id="alignright-tooltip-desc" className="menu-tooltip">{tc(translations.common.format.alignRight)}</span>
                </button>
                <button
                    aria-label={tc(translations.common.format.alignJustify)}
                    type="button"
                    className={classNames(formatMenuBar.alignJustifyButtonState)}
                    onClick={() => alignJustify(quill)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            alignJustify(quill);
                        }
                    }}
                    disabled={formatMenuBar.alignJustifyButtonState === Constants.formatButtonState.disabledAndFocused || formatMenuBar.alignJustifyButtonState === Constants.formatButtonState.disabledAndUnfocused || isLoading}
                >
                    <FontAwesomeIcon className="tw-h-4" size={'sm'} icon={faAlignJustify} />
                    <span role="tooltip" id="alignjustify-tooltip-desc" className="menu-tooltip">{tc(translations.common.format.alignJustify)}</span>
                </button>
            </div>

            <div className="tw-hidden sm:tw-flex tw-items-center tw-gap-x-1">
                <button
                    aria-label={tc(translations.common.format.bulletedList)}
                    type="button"
                    className={classNames(formatMenuBar.listButtonState)}
                    onClick={() => setUnorderedList(quill)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            setUnorderedList(quill);
                        }
                    }}
                    disabled={formatMenuBar.listButtonState === Constants.formatButtonState.disabledAndFocused || formatMenuBar.listButtonState === Constants.formatButtonState.disabledAndUnfocused || isLoading}
                >
                    <FontAwesomeIcon className="tw-h-4" size={'sm'} icon={faList} />
                    <span role="tooltip" id="bulletedlist-tooltip-desc" className="menu-tooltip">{tc(translations.common.format.bulletedList)}</span>
                </button>
                <button
                    aria-label={tc(translations.common.format.numberedList)}
                    type="button"
                    className={classNames(formatMenuBar.numberedListButtonState)}
                    onClick={() => setNumberedList(quill)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            setNumberedList(quill);
                        }
                    }}
                    disabled={formatMenuBar.numberedListButtonState === Constants.formatButtonState.disabledAndFocused || formatMenuBar.numberedListButtonState === Constants.formatButtonState.disabledAndUnfocused || isLoading}
                >
                    <FontAwesomeIcon className="tw-h-4" size={'sm'} icon={faListOl} />
                    <span role="tooltip" id="numberedlist-tooltip-desc" className="menu-tooltip">{tc(translations.common.format.numberedList)}</span>
                </button>
            </div>
            <div className="tw-hidden sm:tw-flex tw-items-center tw-gap-x-1">
                <div
                    className={classNames(formatMenuBar.fontFamilyButtonState, "tw-w-40 tw-h-8 tw-px-2 tw-py-1 tw-inline-flex tw-items-center tw-rounded-sm tw-border tw-border-gray-300 tw-text-xs tw-font-medium tw-cursor-not-allowed ")}
                    disabled={formatMenuBar.fontFamilyButtonState === Constants.formatButtonState.disabledAndFocused || formatMenuBar.fontFamilyButtonState === Constants.formatButtonState.disabledAndUnfocused || isLoading}
                >
                    {formatMenuBar.fontFamilyButtonText}
                    <span role="tooltip" id="font-tooltip-desc" className="menu-tooltip">{tc(translations.common.format.font)}</span>
                </div>
                <div className="menu-select-tooltip-container"
                    disabled={formatMenuBar.fontSizeButtonState === Constants.formatButtonState.disabledAndFocused || formatMenuBar.fontSizeButtonState === Constants.formatButtonState.disabledAndUnfocused || isLoading}
                >
                    <select
                        aria-label={tc(translations.common.format.fontSize)}
                        className={classNames(formatMenuBar.fontSizeButtonState, "tw-w-20 tw-h-8 tw-py-1 tw-inline-flex tw-items-center tw-rounded-sm tw-border tw-border-gray-300 tw-text-xs tw-font-medium focus:tw-ring-indigo-700 focus:tw-border-indigo-700 ")}
                        value={formatMenuBar.fontSizeButtonValue}
                        onChange={(e) => changeFontSize(quill, e.target.value, setFormatMenuBar)}
                        disabled={formatMenuBar.fontSizeButtonState === Constants.formatButtonState.disabledAndFocused || formatMenuBar.fontSizeButtonState === Constants.formatButtonState.disabledAndUnfocused || isLoading}
                    >
                        {Constants.fontSizes.map((fontSize) => (
                            <option key={fontSize} value={fontSize}>{fontSize}</option>
                        ))}
                    </select>
                    <span role="tooltip" id="fontsize-tooltip-desc" className="menu-select-tooltip">{tc(translations.common.format.fontSize)}</span>
                </div>
            </div>

            {/* Mobile menu button */}
            <div className="tw-relative sm:tw-hidden tw-h-6 tw-w-6">
                <Popover className="tw-relative tw-flex-shrink-0">
                    <Popover.Button
                        disabled={isLoading || editorFileRef.current?.DxmlFileStatus !== Constants.fileStatus.draft}
                        className="menu-button-tooltip-container tw-group tw-h-6 tw-w-6 tw-rounded-md tw-inline-flex tw-items-center tw-text-base tw-font-medium">
                        <span className="tw-sr-only">{tc(translations.common.format.openFormatMenu)}</span>
                        <EllipsisHorizontalCircleIcon className="tw-h-6 tw-w-6 tw-text-gray-700" aria-hidden="true" />
                        <span role="tooltip" id="moreoptions-format-tooltip-desc" className="menu-tooltip">{tc(translations.common.moreoptions)}</span>
                    </Popover.Button>
                    <Transition
                        as={Fragment}
                        enter="tw-transition tw-ease-out tw-duration-100"
                        enterFrom="tw-transform tw-opacity-0 tw-scale-95"
                        enterTo="tw-transform tw-opacity-100 tw-scale-100"
                        leave="tw-transition tw-ease-in tw-duration-75"
                        leaveFrom="tw-transform tw-opacity-100 tw-scale-100"
                        leaveTo="tw-transform tw-opacity-0 tw-scale-95"
                    >
                        <Popover.Panel className="tw-origin-top-left tw-absolute tw-left-0 tw-w-48 tw-rounded-sm tw-shadow-lg tw-px-2 tw-py-1 tw-bg-white tw-ring-1 tw-ring-black tw-ring-opacity-5 focus:tw-outline-none tw-transform tw-opacity-100 tw-scale-100 tw-space-y-2">
                            <div className="tw-px-3 tw-py-3 tw-divide-y">
                                <div className="tw-pb-3">
                                    <label className="tw-pl-2 tw-block tw-text-gray-900 tw-text-sm tw-font-medium">{tc(translations.common.format.alignment)}</label>
                                    <div className="tw-flex tw-items-center tw-gap-x-1 tw-py-2">
                                        <button
                                            aria-label={tc(translations.common.format.alignLeft)}
                                            type="button"
                                            className={classNames(formatMenuBar.alignLeftButtonState, "")}
                                            onClick={() => alignLeft(quill)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    alignLeft(quill);
                                                }
                                            }}
                                            disabled={formatMenuBar.alignLeftButtonState === Constants.formatButtonState.disabledAndFocused || formatMenuBar.alignLeftButtonState === Constants.formatButtonState.disabledAndUnfocused || isLoading}
                                        >
                                            <FontAwesomeIcon className="tw-h-4" size={'sm'} icon={faAlignLeft} />
                                            <span role="tooltip" id="alignleft-sm-tooltip-desc" className="menu-tooltip">{tc(translations.common.format.alignLeft)}</span>
                                        </button>
                                        <button
                                            aria-label={tc(translations.common.format.alignCenter)}
                                            type="button"
                                            className={classNames(formatMenuBar.alignCenterButtonState)}
                                            onClick={() => alignCenter(quill)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    alignCenter(quill);
                                                }
                                            }}
                                            disabled={formatMenuBar.alignCenterButtonState === Constants.formatButtonState.disabledAndFocused || formatMenuBar.alignCenterButtonState === Constants.formatButtonState.disabledAndUnfocused || isLoading}
                                        >
                                            <FontAwesomeIcon className="tw-h-4" size={'sm'} icon={faAlignCenter} />
                                            <span role="tooltip" id="aligncenter-sm-tooltip-desc" className="menu-tooltip">{tc(translations.common.format.alignCenter)}</span>
                                        </button>
                                        <button
                                            aria-label={tc(translations.common.format.alignRight)}
                                            type="button"
                                            className={classNames(formatMenuBar.alignRightButtonState)}
                                            onClick={() => alignRight(quill)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    alignRight(quill);
                                                }
                                            }}
                                            disabled={formatMenuBar.alignRightButtonState === Constants.formatButtonState.disabledAndFocused || formatMenuBar.alignRightButtonState === Constants.formatButtonState.disabledAndUnfocused || isLoading}
                                        >
                                            <FontAwesomeIcon className="tw-h-4" size={'sm'} icon={faAlignRight} />
                                            <span role="tooltip" id="alignright-sm-tooltip-desc" className="menu-tooltip">{tc(translations.common.format.alignRight)}</span>
                                        </button>
                                        <button
                                            aria-label={tc(translations.common.format.alignJustify)}
                                            type="button"
                                            className={classNames(formatMenuBar.alignJustifyButtonState)}
                                            onClick={() => alignJustify(quill)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    alignJustify(quill);
                                                }
                                            }}
                                            disabled={formatMenuBar.alignJustifyButtonState === Constants.formatButtonState.disabledAndFocused || formatMenuBar.alignJustifyButtonState === Constants.formatButtonState.disabledAndUnfocused || isLoading}
                                        >
                                            <FontAwesomeIcon className="tw-h-4" size={'sm'} icon={faAlignJustify} />
                                            <span role="tooltip" id="alignjustify-sm-tooltip-desc" className="menu-tooltip">{tc(translations.common.format.alignJustify)}</span>
                                        </button>
                                    </div>
                                </div>

                                <div className="tw-py-3">
                                    <label className="tw-pl-2 tw-block tw-text-gray-900 tw-text-sm tw-font-medium">{tc(translations.common.format.list)}</label>
                                    <div className="tw-flex tw-items-center tw-gap-x-1 tw-py-2">
                                        <button
                                            aria-label={tc(translations.common.format.bulletedList)}
                                            type="button"
                                            className={classNames(formatMenuBar.listButtonState)}
                                            onClick={() => setUnorderedList(quill)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    setUnorderedList(quill);
                                                }
                                            }}
                                            disabled={formatMenuBar.listButtonState === Constants.formatButtonState.disabledAndFocused || formatMenuBar.listButtonState === Constants.formatButtonState.disabledAndUnfocused || isLoading}
                                        >
                                            <FontAwesomeIcon className="tw-h-4" size={'sm'} icon={faList} />
                                            <span role="tooltip" id="bulletedlist-sm-tooltip-desc" className="menu-tooltip">{tc(translations.common.format.bulletedList)}</span>
                                        </button>
                                        <button
                                            aria-label={tc(translations.common.format.numberedList)}
                                            type="button"
                                            className={classNames(formatMenuBar.numberedListButtonState)}
                                            onClick={() => setNumberedList(quill)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    setNumberedList(quill);
                                                }
                                            }}
                                            disabled={formatMenuBar.numberedListButtonState === Constants.formatButtonState.disabledAndFocused || formatMenuBar.numberedListButtonState === Constants.formatButtonState.disabledAndUnfocused || isLoading}
                                        >
                                            <FontAwesomeIcon className="tw-h-4" size={'sm'} icon={faListOl} />
                                            <span role="tooltip" id="numberedlist-sm-tooltip-desc" className="menu-tooltip">{tc(translations.common.format.numberedList)}</span>
                                        </button>
                                    </div>
                                </div>

                                <div className="tw-py-3 tw-px-2">
                                    <div className="tw-flex tw-flex-col tw-gap-y-2">
                                        <div>
                                            <label className="tw-block tw-text-gray-900 tw-text-sm tw-font-medium">{tc(translations.common.format.font)}</label>
                                            <div
                                                className={classNames(formatMenuBar.fontFamilyButtonState, "tw-w-32 tw-h-8 tw-px-2 tw-py-1 tw-inline-flex tw-items-center tw-rounded-sm tw-border tw-border-gray-300 tw-text-xs tw-font-medium tw-cursor-not-allowed ")}
                                                disabled={formatMenuBar.fontFamilyButtonState === Constants.formatButtonState.disabledAndFocused || formatMenuBar.fontFamilyButtonState === Constants.formatButtonState.disabledAndUnfocused || isLoading}
                                            >
                                                {formatMenuBar.fontFamilyButtonText}
                                            </div>

                                        </div>
                                        <div>
                                            <label className="tw-block tw-text-gray-900 tw-text-sm tw-font-medium">{tc(translations.common.format.size)}</label>
                                            <select
                                                aria-label={tc(translations.common.format.fontSize)}
                                                title={tc(translations.common.format.fontSize)}
                                                className={classNames(formatMenuBar.fontSizeButtonState, "tw-w-32 tw-h-8 tw-py-1 tw-inline-flex tw-items-center tw-rounded-sm tw-border tw-border-gray-300 tw-text-xs tw-font-medium focus:tw-ring-indigo-700 focus:tw-border-indigo-700 ")}
                                                value={formatMenuBar.fontSizeButtonValue}
                                                onChange={(e) => changeFontSize(quill, e.target.value, setFormatMenuBar)}
                                                disabled={formatMenuBar.fontSizeButtonState === Constants.formatButtonState.disabledAndFocused || formatMenuBar.fontSizeButtonState === Constants.formatButtonState.disabledAndUnfocused || isLoading}
                                            >
                                                {Constants.fontSizes.map((fontSize) => (
                                                    <option key={fontSize} value={fontSize}>{fontSize}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Popover.Panel>
                    </Transition>
                </Popover>
            </div>
        </div>
    )
}